import { named, withDependencies } from '@wix/thunderbolt-ioc'
import fastdom from 'fastdom'
import {
	BrowserWindowSymbol,
	BrowserWindow,
	PageFeatureConfigSymbol,
	MasterPageFeatureConfigSymbol,
} from '@wix/thunderbolt-symbols'
import { constants } from '@wix/thunderbolt-becky-root'
import { name } from './symbols'
import type {
	IStickyToComponent,
	SCROLL_DIRECTION,
	StickyToComponentMasterPageConfig,
	StickyToComponentPageConfig,
} from './types'

const { SITE_CONTAINER_WITH_HEADER_SELECTOR } = constants

export const StickyToComponent = withDependencies(
	[named(PageFeatureConfigSymbol, name), named(MasterPageFeatureConfigSymbol, name), BrowserWindowSymbol],
	(
		stickyToComponentPageConfig: StickyToComponentPageConfig,
		masterPageConfig: StickyToComponentMasterPageConfig,
		browserWindow: NonNullable<BrowserWindow>
	): IStickyToComponent => {
		const { isResponsive } = stickyToComponentPageConfig

		let headerResizeObserver: ResizeObserver
		let scrollDirection: SCROLL_DIRECTION
		let scrollY = browserWindow.scrollY

		const getHeaderElement = () => {
			const [firstHeader] = Array.from(browserWindow.document.getElementsByTagName('header'))
			return firstHeader
		}

		const isHeaderElementExists = () => !!getHeaderElement()

		const initiateHeaderHeightVar = () => {
			const header = getHeaderElement()
			fastdom.measure(() => {
				const headerHeight = header.getBoundingClientRect().height
				setHeaderHeightVar(headerHeight)
			})
		}

		const setHeaderHeightVar = (height: number | string) => {
			fastdom.mutate(() => {
				const siteContainer = browserWindow.document.querySelector(
					SITE_CONTAINER_WITH_HEADER_SELECTOR
				) as HTMLElement
				siteContainer && siteContainer.style.setProperty('--header-height', `${height}px`)
			})
		}

		const observeHeaderHeight = () => {
			const header = getHeaderElement()
			headerResizeObserver = new ResizeObserver(() => {
				fastdom.measure(() => {
					const headerHeight = header.getBoundingClientRect().height
					setHeaderHeightVar(headerHeight!)
				})
			})
			headerResizeObserver.observe(header)
		}

		const transitionStartHandler = () => {
			scrollY = browserWindow.scrollY
		}

		const transitionEndHandler = () => {
			const newScrollY = browserWindow.scrollY

			if (!scrollDirection && !newScrollY) {
				return
			}

			if (scrollY > newScrollY) {
				scrollDirection = 'UP'
			} else if (scrollY < newScrollY) {
				scrollDirection = 'DOWN'
			}

			setHeaderHeightVar(scrollDirection === 'UP' ? masterPageConfig.classicFixedHeaderHeight : 0)
		}

		const listenToTransition = () => {
			const header = getHeaderElement()
			header.addEventListener('transitionstart', transitionStartHandler)
			header.addEventListener('transitionend', transitionEndHandler)
		}

		const stopListenToTransition = () => {
			const header = getHeaderElement()
			header.removeEventListener('transitionstart', transitionEndHandler)
			header.removeEventListener('transitionend', transitionEndHandler)
		}

		const unobserveHeaderHeight = () => {
			const header = getHeaderElement()
			headerResizeObserver.unobserve(header)
		}

		return {
			initHeaderObserver() {
				if (isResponsive && isHeaderElementExists()) {
					initiateHeaderHeightVar()
					observeHeaderHeight()
				}

				if (masterPageConfig.shouldListenToTransitionEnd) {
					listenToTransition()
				}
			},
			destroyHeaderObserver() {
				if (masterPageConfig.shouldListenToTransitionEnd) {
					stopListenToTransition()
				}

				if (isResponsive && isHeaderElementExists()) {
					unobserveHeaderHeight()
				}
			},
			restoreHeaderHeightVar() {
				if (masterPageConfig.shouldListenToTransitionEnd) {
					setHeaderHeightVar(masterPageConfig.classicFixedHeaderHeight)
				}
			},
		}
	}
)
